import {
  AlertTriangle,
  BarChart3,
  BookmarkCheck,
  Brain,
  CalendarDays,
  Check,
  ChevronRight,
  FileText,
  Hash,
  Heading1,
  Image,
  LayoutGrid,
  LifeBuoy,
  Loader2,
  MousePointerClick,
  Palette,
  Paperclip,
  PlugZap,
  RefreshCw,
  Sparkles,
  SquarePen,
  Type,
  Undo2,
  Video,
  X,
  Zap,
} from 'lucide-react'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
// Brain is used in ThinkingBlock, Zap is the fallback tool icon
import { AnimatePresence, motion } from 'framer-motion'
import { useChat, type UIMessage } from '@ai-sdk/react'
import { lastAssistantMessageIsCompleteWithApprovalResponses } from 'ai'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { PusherEvent } from '@mondaygpt/constants'
import { isUserMediaContent } from '@mondaygpt/types'
import { useListenToPusherEvent } from '../../core/hooks/useListenToPusherEvent'
import { DefaultChatTransport } from 'ai'
import { ComposerRow, ComposerSendButton, platformLabel } from 'components'
import { useAnalytics } from 'hooks'
import { usePasteImage } from 'hooks/use-paste-image'
import {
  ComposerCreditBlock,
  selectIsOutOfCredits,
  selectTotalCredits,
  useSubscriptionGate,
} from 'modules/billing'
import { useAppSelector } from 'modules/core'
import { apiCall } from 'modules/core/services/api'
import { OpenAPI } from 'openapi'
import { CalendarService } from 'openapi/services/CalendarService'
import { WeeklyQueueService } from 'openapi/services/WeeklyQueueService'
import { SCHEDULE_TOOL_ACTIVITY_EVENT } from '../assistant-chat-events'
import { useScheduleTimezone } from '../../common/hooks/use-brand-timezone'
import { formatInBusinessTz } from '../../common/utils/business-timezone'
import { CreativeService } from 'openapi/services/CreativeService'
import { ProjectService } from 'openapi/services/ProjectService'
import { ulid } from 'ulid'
import { MAX_CHAT_ATTACHMENTS, usePendingAttachments } from '../hooks/usePendingAttachments'
import { useChatStatusStore } from '../store/chat-status-store'
import {
  useChatPrefillStore,
  useInitialPromptStore,
  useQueuedPromptStore,
  useSectionContextStore,
  type SectionContext,
} from '../store/conversation-store'
import { useCreativeStore } from '../store/creative-store'
import { useIdeaStore } from '../store/idea-store'
import { useCreativeProcessStore } from '../store/creative-process-store'
import { isShipVerdict } from '../process/creative-process.types'
import { verdictHeadline } from '../process/score-explainers'
import { FixWithTakaCard } from './process/FixWithTakaCard'
import { useComposerEditor } from '../composition/editor/CompositionEditorProvider'
import { useGenerationPhaseStore } from '../store/generation-phase-store'
import { useGenerationStatusStore } from '../store/generation-status-store'
import { CreativeType, creativeTypeMeta, CREATOR_MODE_MAP, type InstagramMediaItem } from '../types'
import { useVideoProgress } from '../hooks/useVideoProgress'
import {
  applyEditedCarouselSlide,
  buildCarouselContentPatch,
} from '../utils/applyCarouselToolOutput'
import { buildEmptyResponseDiagnostics } from '../utils/empty-response-diagnostics'
import { API_BASE, OPTIONS_RE } from '../utils/chat-markers'
import { CreativeGenerationCard } from './CreativeGenerationCard'
import { SupportHandoffCard, type SupportHandoffStatus } from './SupportHandoffCard'
import { useComposerReadOnly } from './composer-read-only'
import { MarkdownContent } from './MarkdownContent'
import { GenerationSteps } from './GenerationSteps'
import { scoreTier, verdictLabel } from '../process/creative-process.types'
import { tierColor } from './process/tier-color'
import { buildPhaseSteps, type GenerationPhaseEvent } from '../process/phase-steps'

/** SocialKit engine tools whose result carries a fresh rubric score. */
const SOCIALKIT_TOOLS = new Set([
  'revisePost',
  'generatePost',
  'regenerateImage',
  'reviseCarousel',
  'generateCarousel',
  // Reels generate + revise through SocialKit — they stream the live-ladder steps (incl.
  // the judge) and carry a score, so they show the durable process disclosure + score chip.
  'generateReel',
  'reviseReel',
])

// ─── Tool → Content Field Mapping ─────────────────────────────────────────

/** Maps AI tool names to the content fields they affect (for per-field shimmer) */
const TOOL_FIELD_MAP: Record<string, string[]> = {
  updateCaption: ['caption'],
  updateHashtags: ['hashtags'],
  generateImage: ['image'],
  generateInstagramCarousel: ['image'],
  editCarouselSlide: ['image'],
  generateVideo: ['video'],
  updateImageDescription: ['image'],
  setImage: ['image'],
  // SocialKit engine tools — a revise re-renders the image + can change the copy.
  revisePost: ['caption', 'hashtags', 'image'],
  regenerateImage: ['image'],
  generatePost: ['image', 'caption', 'hashtags'],
  // SocialKit carousel tools — touch the deck (image) + caption/hashtags.
  reviseCarousel: ['caption', 'hashtags', 'image'],
  generateCarousel: ['image', 'caption', 'hashtags'],
  // SocialKit reel tools — generate/re-render the video + caption/hashtags.
  generateReel: ['video', 'caption', 'hashtags'],
  reviseReel: ['video', 'caption', 'hashtags'],
  // Version restore brings the whole state back (deck/master + caption/hashtags).
  restoreVersion: ['image', 'caption', 'hashtags'],
}

/** Resolve which content fields a tool invocation affects */
function getFieldsFromToolInvocation(toolName: string, args?: Record<string, unknown>): string[] {
  const mapped = TOOL_FIELD_MAP[toolName]
  if (mapped) return mapped

  // updateContent: inspect args.fields for specific keys
  if (toolName === 'updateContent') {
    if (args?.fields && typeof args.fields === 'object') {
      const keys = Object.keys(args.fields as Record<string, unknown>)
      if (keys.length > 0) return keys
    }
    // Fallback: return the args keys directly (works for any creative type)
    if (args && typeof args === 'object') {
      const keys = Object.keys(args as Record<string, unknown>)
      if (keys.length > 0) return keys
    }
    return []
  }

  return []
}

// ─── Thinking Block ──────────────────────────────────────────────────────

export function ThinkingBlock({ isStreaming }: { text: string; isStreaming: boolean }) {
  const [elapsed, setElapsed] = useState(0)
  const startRef = useRef(Date.now())
  const finalElapsed = useRef<number | null>(null)

  useEffect(() => {
    if (isStreaming) {
      startRef.current = Date.now()
      finalElapsed.current = null
      const interval = setInterval(() => {
        setElapsed(Math.round((Date.now() - startRef.current) / 1000))
      }, 1000)
      return () => clearInterval(interval)
    } else if (finalElapsed.current === null) {
      finalElapsed.current = Math.max(1, Math.round((Date.now() - startRef.current) / 1000))
      setElapsed(finalElapsed.current)
    }
  }, [isStreaming])

  if (!isStreaming && elapsed === 0) return null

  return (
    <div className="mb-2">
      <div className="flex items-center gap-1.5 rounded-[6px] px-2 py-1 text-[11px] text-paper-faint">
        {isStreaming ? (
          <Sparkles size={12} className="flex-shrink-0 text-paper-faint" />
        ) : (
          <Brain size={12} className="flex-shrink-0 text-paper-faint" />
        )}
        <span className="flex-1 text-left">
          {isStreaming ? (
            <span className="shimmer-text">{`Thinking${elapsed > 0 ? ` (${elapsed}s)` : ''}`}</span>
          ) : (
            `Thought for ${elapsed}s`
          )}
        </span>
      </div>
    </div>
  )
}

// ─── Thinking Bubble ──────────────────────────────────────────────────────

export function ThinkingBubble({
  icon: Icon = Sparkles,
  label = 'Thinking',
}: {
  icon?: typeof Sparkles
  label?: string
}) {
  return (
    <div className="mb-3 flex justify-start">
      <div className="flex items-center gap-2 rounded-[6px] px-2 py-1.5 text-[11px] text-paper-faint">
        <Icon size={12} className="flex-shrink-0 text-paper-faint" />
        <span className="shimmer-text">{label}</span>
      </div>
    </div>
  )
}

// ─── Tool Call Indicator ───────────────────────────────────────────────────

const TOOL_META: Record<
  string,
  { label: string; activeLabel: string; asyncLabel?: string; icon: typeof SquarePen }
> = {
  // Repair-hook fallback for calls to tools a room doesn't carry (server
  // reroutes AI_NoSuchToolError here so the turn survives). Always a soft
  // failure, so the chip renders the tool output's user-safe `message`
  // ("That action is not available in this chat."); label/activeLabel only
  // cover the transient in-flight state.
  unavailableToolFallback: {
    label: 'That action is not available here',
    activeLabel: 'Checking that action',
    icon: Zap,
  },
  updateCaption: { label: 'Wrote caption', activeLabel: 'Writing caption', icon: SquarePen },
  updateHashtags: { label: 'Added hashtags', activeLabel: 'Picking hashtags', icon: Hash },
  updateImageDescription: {
    label: 'Described image',
    activeLabel: 'Describing image',
    icon: Image,
  },
  updateContent: { label: 'Made changes', activeLabel: 'Making changes', icon: FileText },
  generateImage: { label: 'Created image', activeLabel: 'Creating image', icon: Image },
  generateInstagramCarousel: {
    label: 'Created carousel',
    activeLabel: 'Creating carousel',
    icon: Image,
  },
  editCarouselSlide: {
    label: 'Updated slide',
    activeLabel: 'Editing slide',
    icon: Image,
  },
  generateVideo: { label: 'Created video', activeLabel: 'Creating video', icon: Video },
  setImage: { label: 'Set image', activeLabel: 'Setting image', icon: Image },
  renameCreative: { label: 'Named creative', activeLabel: 'Naming creative', icon: SquarePen },
  // SocialKit engine tools.
  revisePost: { label: 'Revised the post', activeLabel: 'Revising with SocialKit', icon: Sparkles },
  regenerateImage: { label: 'Remade the image', activeLabel: 'Remaking the image', icon: Image },
  generatePost: {
    label: 'Designed the post',
    activeLabel: 'Designing with SocialKit',
    icon: Sparkles,
  },
  reviseCarousel: {
    label: 'Revised the carousel',
    activeLabel: 'Revising the carousel',
    icon: Sparkles,
  },
  generateCarousel: {
    label: 'Designed the carousel',
    activeLabel: 'Designing the carousel',
    icon: Sparkles,
  },
  restoreVersion: {
    label: 'Restored a version',
    activeLabel: 'Bringing that version back',
    icon: Undo2,
  },
  generateReel: {
    label: 'Created the reel',
    activeLabel: 'Creating the reel',
    // Generation continues in the background (draft filming / deferred full render) —
    // the finished tool part only confirms the START.
    asyncLabel: 'Reel started, filming scenes',
    icon: Video,
  },
  reviseReel: {
    label: 'Re-rendered the reel',
    activeLabel: 'Re-rendering the reel',
    // The revise runs as a background job — the tool result only confirms the START.
    asyncLabel: 'Reel revision started',
    icon: Video,
  },
  // Operator reads/actions + memory (the unified agent). These lines are trust
  // RECEIPTS phrased as completed human actions — never internal tool names.
  getContentCalendar: {
    label: 'Checked your calendar',
    activeLabel: 'Checking your calendar',
    icon: CalendarDays,
  },
  getAccountStatus: {
    label: 'Checked your account',
    activeLabel: 'Checking your account',
    icon: PlugZap,
  },
  getRecentPerformance: {
    label: 'Checked recent performance',
    activeLabel: 'Checking recent performance',
    icon: BarChart3,
  },
  reschedulePost: {
    label: 'Moved a post',
    activeLabel: 'Moving the post',
    icon: CalendarDays,
  },
  captureBrandMemory: {
    label: 'Saved to your brand rules',
    activeLabel: 'Saving to your brand rules',
    icon: BookmarkCheck,
  },
  patchBrandCard: {
    label: 'Updated your brand profile',
    activeLabel: 'Updating your brand profile',
    icon: SquarePen,
  },
  setBrandTone: {
    label: 'Set your brand tone',
    activeLabel: 'Setting your brand tone',
    icon: Type,
  },
  setBrandColors: {
    label: 'Updated your brand colors',
    activeLabel: 'Updating your brand colors',
    icon: Palette,
  },
  getBrandImages: {
    label: 'Looked through your photos',
    activeLabel: 'Looking through your photos',
    icon: Image,
  },
  escalateToSupport: {
    label: 'Contacted the support team',
    activeLabel: 'Contacting the support team',
    icon: LifeBuoy,
  },
}

/**
 * Should this tool's indicator render even where creation-tool chrome is hidden
 * (the assistant chat passes hideToolIndicators and renders its own cards)?
 * Driven by TOOL_META so a new tool with a human label automatically shows as a
 * receipt in every room — no second hardcoded list to drift.
 */
function isReceiptTool(toolName: string): boolean {
  return toolName !== 'createSocialPost' && !!TOOL_META[toolName]
}

/**
 * The finished live ladder, minimized into the thread: a quiet, collapsed
 * "N steps" disclosure on a completed SocialKit result. Expands to the same
 * beats the live ladder showed (decide → imagery → design → judge → revise),
 * so the process never just vanishes — it folds away and stays inspectable.
 */
function ProcessDisclosure({ events }: { events: GenerationPhaseEvent[] }) {
  const [open, setOpen] = useState(false)
  const steps = useMemo(() => buildPhaseSteps(events), [events])
  if (steps.length === 0) return null

  return (
    <div className="mt-0.5 ml-5">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 text-[11px] text-paper-faint transition-colors hover:text-paper-dim"
      >
        <ChevronRight
          size={11}
          className={`flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
        <span>
          {steps.length} {steps.length === 1 ? 'step' : 'steps'}
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <ol className="mt-1 space-y-1">
            {steps.map(s => (
              <li key={s.id} className="flex items-start gap-1.5 text-[11px]">
                <Check size={11} className="text-success mt-[3px] flex-shrink-0" />
                <span className="text-paper-dim">
                  {s.label}
                  {s.detail && <span className="text-paper-faint"> · {s.detail}</span>}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  )
}

/** Wall-clock formatter for the approval card: renders the BUSINESS-local time
 * carried in the ISO string (never the viewer's timezone). */
function formatWallClock(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!m) return iso
  const [, y, mo, d, h, min] = m
  const wall = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(min))
  return wall.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const UNDO_WINDOW_MS = 10 * 60_000

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

/**
 * The tappable half of a receipt: what the read/action actually returned,
 * rebuilt from the persisted tool output. Receipts stay one quiet line until
 * tapped — then they open into the evidence (the posts read, the connections
 * checked, the rule saved), like a citation you can click into. Works on
 * restored history too: the artifact is the record of what was true THEN.
 */
function receiptDetailLines(
  toolName: string,
  output: Record<string, unknown> | undefined
): string[] | null {
  if (!output) return null
  const lines: string[] = []

  if (toolName === 'getContentCalendar') {
    const items = Array.isArray(output.items)
      ? (output.items as {
          name?: string
          platform?: string
          status?: string
          scheduledAt?: string
          publishedAt?: string
        }[])
      : []
    for (const item of items.slice(0, 6)) {
      const when = item.scheduledAt
        ? formatWallClock(item.scheduledAt)
        : item.publishedAt
          ? `published ${formatWallClock(item.publishedAt)}`
          : (item.status ?? '')
      lines.push(`${item.name ?? 'Untitled'} · ${platformLabel(item.platform ?? '')} · ${when}`)
    }
    if (items.length > 6) lines.push(`+${items.length - 6} more`)
    if (items.length === 0) lines.push('Nothing scheduled in this window')
    const drafts = output.unscheduledDrafts as { count?: number } | undefined
    if (drafts?.count)
      lines.push(`${drafts.count} draft${drafts.count === 1 ? '' : 's'} in progress`)
    return lines
  }

  if (toolName === 'getAccountStatus') {
    const connections = output.connections as
      | {
          platform: string
          connected: boolean
          account?: string
          needsReconnect?: boolean
          restricted?: boolean
        }[]
      | null
      | undefined
    if (connections === null) {
      lines.push('Couldn’t read connections')
    } else if (Array.isArray(connections)) {
      // Restricted accounts ARE connected (only publishing is paused): keep
      // them in the list, marked, so the card never reads "No accounts
      // connected" next to a paused one.
      const connected = connections.filter(c => c.connected)
      lines.push(
        connected.length
          ? `Connected: ${connected
              .map(
                c =>
                  `${platformLabel(c.platform)}${c.account ? ` (${c.account})` : ''}${c.restricted ? ' · publishing paused' : ''}`
              )
              .join(', ')}`
          : 'No accounts connected'
      )
      const reconnect = connections.filter(c => c.needsReconnect)
      if (reconnect.length)
        lines.push(`Needs reconnect: ${reconnect.map(c => platformLabel(c.platform)).join(', ')}`)
    }
    const plans = Array.isArray(output.plans)
      ? (output.plans as { channel?: string; frequency?: string; publishMode?: string }[])
      : []
    for (const plan of plans.slice(0, 3)) {
      lines.push(
        [platformLabel(plan.channel ?? ''), plan.frequency, plan.publishMode]
          .filter(Boolean)
          .join(' · ')
      )
    }
    const subscription = output.subscription as { tier?: string } | null | undefined
    if (subscription?.tier) lines.push(`${capitalize(subscription.tier)} plan`)
    const credits = output.credits as { total?: number } | null | undefined
    if (credits && typeof credits.total === 'number') lines.push(`${credits.total} credits left`)
    return lines.length ? lines : null
  }

  if (toolName === 'getRecentPerformance') {
    const posts = Array.isArray(output.posts)
      ? (output.posts as {
          platform?: string
          likes?: number
          comments?: number
          publishedAt?: string
        }[])
      : []
    for (const post of posts.slice(0, 5)) {
      lines.push(
        `${capitalize(post.platform ?? '')} · ${post.likes ?? 0} likes · ${post.comments ?? 0} comments · ${
          post.publishedAt ? formatWallClock(post.publishedAt) : ''
        }`
      )
    }
    if (posts.length === 0) lines.push('No published posts with metrics yet')
    return lines
  }

  if (toolName === 'reschedulePost' && output.success === true) {
    const to = typeof output.scheduledAt === 'string' ? formatWallClock(output.scheduledAt) : null
    const from =
      typeof output.previousScheduledAt === 'string'
        ? formatWallClock(output.previousScheduledAt)
        : output.previousScheduledAt === null
          ? 'drafts'
          : null
    if (to && from) return [`From ${from} to ${to}`]
    return null
  }

  if (toolName === 'captureBrandMemory' && output.success === true) {
    const learned = Array.isArray(output.learned) ? (output.learned as string[]) : []
    return learned.length ? learned.slice(0, 4) : null
  }

  return null
}

/**
 * Time-limited Undo under a successful "Moved a post" receipt. Deterministic
 * put-back through the calendar REST endpoints, never a model turn — undo must
 * be instant and exact. Only rendered for receipts that completed live in this
 * mount (history restores get no dead buttons), and never restores a slot that
 * already passed, because the publish sweep would ship it immediately.
 */
function RescheduleUndo({ output }: { output: Record<string, unknown> }) {
  const creativeId = typeof output.creativeId === 'string' ? output.creativeId : null
  const previous =
    typeof output.previousScheduledAt === 'string' ? output.previousScheduledAt : null
  const wasDraft = output.previousScheduledAt === null
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'failed' | 'expired'>('idle')

  useEffect(() => {
    const t = setTimeout(() => setState(s => (s === 'idle' ? 'expired' : s)), UNDO_WINDOW_MS)
    return () => clearTimeout(t)
  }, [])

  const previousIsRestorable = () =>
    !!previous && new Date(previous).getTime() > Date.now() + 120_000

  // Pre-undo-feature results (no previousScheduledAt key) or an unrestorable
  // past slot: no affordance at all.
  if (!creativeId || (!wasDraft && !previous)) return null
  if (state === 'idle' && !wasDraft && !previousIsRestorable()) return null
  if (state === 'expired') return null

  const handleUndo = async () => {
    setState('busy')
    try {
      if (wasDraft) {
        await CalendarService.calendarControllerUnscheduleCreativeV1(creativeId)
      } else if (previousIsRestorable()) {
        // Same write path the agent's move used (NOT the bare calendar
        // endpoint): WeeklyQueueService.reschedule maintains the autopilot
        // skipped-slot tombstones — a bare scheduledAt write leaves the
        // vacated day un-tombstoned and the backfill regenerates a post
        // into the exact slot the user just undid out of.
        await WeeklyQueueService.weeklyQueueControllerRescheduleV1(creativeId, {
          scheduledAt: previous!,
        })
      } else {
        setState('failed')
        return
      }
      // The grid behind the panel follows the move live; it must follow the
      // undo the same way.
      window.dispatchEvent(new CustomEvent(SCHEDULE_TOOL_ACTIVITY_EVENT))
      setState('done')
    } catch (err) {
      console.error('Undo reschedule failed:', err)
      setState('failed')
    }
  }

  const backTo = wasDraft ? 'back to drafts' : `back to ${formatWallClock(previous!)}`

  return (
    <div className="mt-0.5 ml-5 flex items-center gap-1 text-[11px] text-paper-faint">
      {state === 'idle' && (
        <button
          type="button"
          onClick={handleUndo}
          className="flex items-center gap-1 text-paper-dim transition-colors hover:text-paper"
        >
          <Undo2 size={10} className="flex-shrink-0" />
          <span>Undo · {backTo}</span>
        </button>
      )}
      {state === 'busy' && <span className="shimmer-text">Putting it back</span>}
      {state === 'done' && (
        <>
          <Check size={10} className="text-success flex-shrink-0" />
          <span>{wasDraft ? 'Back in drafts.' : `Back to ${formatWallClock(previous!)}.`}</span>
        </>
      )}
      {state === 'failed' && <span>Couldn’t undo. Move it from the calendar.</span>}
    </div>
  )
}

/**
 * Preview-then-apply card for a tool call paused on user approval (AI SDK v7
 * HITL). The card IS the question — Taka calls the tool, the user decides.
 */
function ToolApprovalCard({
  part,
  onRespond,
}: {
  part: Record<string, unknown>
  onRespond?: (args: { id: string; approved: boolean }) => void
}) {
  const scheduleTimezone = useScheduleTimezone()
  const toolName =
    typeof part.toolName === 'string'
      ? part.toolName
      : typeof part.type === 'string' && (part.type as string).startsWith('tool-')
        ? (part.type as string).slice(5)
        : ''
  const approval = part.approval as { id: string; approved?: boolean } | undefined
  const input = (part.input ?? {}) as Record<string, unknown>
  if (!approval?.id) return null

  // Answered: collapse to a quiet trace — dead disabled buttons read as broken.
  if (part.state === 'approval-responded') {
    // A RESTORED approval-responded part means execution never persisted (an
    // executed approval evolves to output-available). Claiming "On it." for an
    // action that will never run is the dishonesty class this flow exists to
    // kill — say plainly that it didn't happen.
    if (!onRespond && approval.approved) {
      return (
        <div className="mb-1 px-2 py-1 text-[11px] text-paper-faint">
          You confirmed this, but it didn't run. Ask again if you still want it.
        </div>
      )
    }
    return (
      <div className="mb-1 px-2 py-1 text-[11px] text-paper-faint">
        {approval.approved ? 'Confirmed. On it.' : 'Kept as scheduled.'}
      </div>
    )
  }
  // Restored from history with no live responder: the moment passed — render
  // an expired trace, never an active-looking card that can't do anything.
  if (!onRespond) {
    return (
      <div className="mb-1 px-2 py-1 text-[11px] text-paper-faint">
        This confirmation expired. Ask again if you still want it.
      </div>
    )
  }

  const isReschedule = toolName === 'reschedulePost'
  // Name the post on the card — "this post" is ambiguous the moment more than
  // one post is in play (e.g. picking a draft to schedule).
  const postLabel =
    typeof input.postName === 'string' && input.postName.trim()
      ? `"${input.postName.trim()}"`
      : 'this post'
  // input.scheduledAt is MODEL-authored: the prompt asks for a business-local
  // offset but a UTC 'Z' is equally valid ISO, and wall-clock-formatting that
  // shows the WRONG time on the very card the user approves. Convert the
  // instant into the business timezone first; the wall-clock fallback only
  // covers a missing timezone.
  const cardTime = (iso: string) =>
    scheduleTimezone
      ? formatInBusinessTz(iso, scheduleTimezone, 'EEE, MMM d, h:mm a')
      : formatWallClock(iso)
  const question = isReschedule
    ? typeof input.scheduledAt === 'string'
      ? `Move ${postLabel} to ${cardTime(input.scheduledAt)}?`
      : `Move ${postLabel}?`
    : `Run ${TOOL_META[toolName]?.activeLabel?.toLowerCase() ?? toolName}?`

  return (
    <div className="mb-2 rounded-[10px] border border-line-bright bg-bg-2 px-3 py-2.5">
      <div className="mb-2 flex items-center gap-2">
        <CalendarDays size={14} className="flex-shrink-0 text-acid" />
        <span className="text-[12.5px] font-medium text-paper">{question}</span>
      </div>
      <div className="flex gap-1.5">
        <button
          type="button"
          disabled={!onRespond}
          onClick={() => onRespond?.({ id: approval.id, approved: true })}
          className="text-ink-on-acid rounded-[8px] bg-acid px-3 py-1.5 text-[12px] font-semibold transition-opacity disabled:opacity-40"
        >
          {isReschedule ? 'Move it' : 'Go ahead'}
        </button>
        <button
          type="button"
          disabled={!onRespond}
          onClick={() => onRespond?.({ id: approval.id, approved: false })}
          className="rounded-[8px] border border-line px-3 py-1.5 text-[12px] font-medium text-paper-dim transition-colors hover:bg-bg-3 disabled:opacity-40"
        >
          {isReschedule ? 'Keep it' : 'Not now'}
        </button>
      </div>
    </div>
  )
}

function ToolCallIndicator({
  part,
  live = false,
}: {
  part: Record<string, unknown>
  /** True when this part belongs to a message streamed live in this session (not restored history) — gates live-moment affordances like Undo. */
  live?: boolean
}) {
  const toolName =
    typeof part.toolName === 'string'
      ? part.toolName
      : typeof part.type === 'string' && (part.type as string).startsWith('tool-')
        ? (part.type as string).slice(5)
        : ''

  const meta = TOOL_META[toolName] || {
    label: `Used ${toolName}`,
    activeLabel: `Running ${toolName}`,
    icon: Zap,
  }
  const Icon = meta.icon
  const isDone = part.state === 'output-available'
  const isHardError = part.state === 'output-error'
  const output = part.output as Record<string, unknown> | undefined

  const isImageTool = toolName === 'generateImage' || toolName === 'setImage'
  const isVideoTool = toolName === 'generateVideo'
  const isSocialKitTool = SOCIALKIT_TOOLS.has(toolName)
  // ANY tool that reports success:false failed — the indicator must never wear
  // its happy label ("Moved a post") over a failed result. An `error` string
  // counts too: operator reads historically returned {type, error} without the
  // flag, and those results replay from persisted threads forever.
  const isSoftFailure = isDone && (output?.success === false || typeof output?.error === 'string')
  const isError = isHardError || isSoftFailure
  const isWorking = !isDone && !isHardError

  // SocialKit revise/generate carry a fresh rubric score — show the delta inline.
  const skScore =
    isDone && isSocialKitTool && output?.success && output?.score
      ? (output.score as { score: number; verdict: string })
      : null
  const skScoreBefore = typeof output?.scoreBefore === 'number' ? output.scoreBefore : undefined

  // A revise/regenerate that merged the user's manual canvas edits flags it so the chat
  // can reassure them deterministically (not relying on the model to repeat the message).
  const skPreservedEdits =
    isDone && isSocialKitTool && output?.success === true && output?.preservedUserEdits === true

  // The streamed pipeline beats, kept on a finished SocialKit result so the live
  // ladder minimizes into a durable, expandable disclosure (not just disappears).
  const skSteps =
    isDone && isSocialKitTool && output?.success && Array.isArray(output?.steps)
      ? (output.steps as GenerationPhaseEvent[])
      : null

  const videoProgressMessage = useVideoProgress(isVideoTool && isWorking)
  const navigate = useNavigate()

  // Collapsible image preview for image generation/set results
  const imageUrl = isDone && isImageTool && output?.success ? (output.imageUrl as string) : null
  // Tappable receipt artifact: the read/action's evidence, from the tool output.
  const detailLines = isDone && !isError ? receiptDetailLines(toolName, output) : null
  const hasDetail = !!imageUrl || !!detailLines?.length

  // Start collapsed. Only auto-expand when the image URL arrives *after* mount
  // (i.e. during a live stream), not when restoring from history.
  const [isExpanded, setIsExpanded] = useState(false)
  const mountedWithImage = useRef(!!imageUrl)

  useEffect(() => {
    // Image was already present on mount (page reload) — stay collapsed
    if (mountedWithImage.current) return
    if (imageUrl) {
      setIsExpanded(true)
      const timer = setTimeout(() => setIsExpanded(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [imageUrl])

  return (
    <div className="mb-1">
      <button
        onClick={hasDetail ? () => setIsExpanded(v => !v) : undefined}
        className={`group flex w-full items-center gap-1.5 rounded-[6px] px-2 py-1 text-[11px] transition-all duration-300 ${
          isSoftFailure
            ? 'text-[color:var(--warning)]'
            : isHardError
              ? 'text-[color:var(--alert-error-text)]'
              : hasDetail
                ? 'text-paper-dim hover:bg-bg-3 hover:text-paper'
                : 'text-paper-dim'
        } ${hasDetail ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {isError ? (
          <AlertTriangle size={12} className="flex-shrink-0" />
        ) : isDone ? (
          <Check size={12} className="text-success flex-shrink-0" />
        ) : (
          <Icon size={12} className="flex-shrink-0 animate-pulse text-paper-faint" />
        )}
        <span className="flex-1 text-left">
          {isWorking ? (
            <span className="shimmer-text">
              {isVideoTool ? videoProgressMessage : meta.activeLabel}
            </span>
          ) : isSoftFailure ? (
            isSocialKitTool ? (
              (output?.message as string) || 'SocialKit couldn’t make that change'
            ) : isVideoTool ? (
              'Video generation failed'
            ) : isImageTool ? (
              'Image generation failed'
            ) : (
              ((output?.error || output?.message) as string) || 'Couldn’t finish that'
            )
          ) : output?.async === true && meta.asyncLabel ? (
            // A background job: the finished tool part only confirms the START — never
            // show the completed label before the work actually lands.
            meta.asyncLabel
          ) : (
            meta.label
          )}
        </span>
        {hasDetail && (
          <ChevronRight
            size={12}
            className={`flex-shrink-0 text-paper-faint transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
          />
        )}
      </button>
      {skScore && (
        <div className="mt-0.5 ml-5 flex items-center gap-1.5 text-[11px]">
          {typeof skScoreBefore === 'number' && skScoreBefore !== skScore.score && (
            <span className="text-paper-faint tabular-nums">{skScoreBefore} →</span>
          )}
          <span
            className="font-semibold tabular-nums"
            style={{ color: tierColor(scoreTier(skScore.score)) }}
          >
            {skScore.score}
          </span>
          <span className="text-paper-faint">· {verdictLabel(skScore.verdict)}</span>
        </div>
      )}
      {skPreservedEdits && (
        <div className="mt-0.5 ml-5 flex items-center gap-1 text-[11px] text-paper-faint">
          <Check size={10} className="text-success flex-shrink-0" />
          <span>Kept your layout edits</span>
        </div>
      )}
      {toolName === 'reschedulePost' && isDone && output?.success === true && live && (
        <RescheduleUndo output={output} />
      )}
      {toolName === 'captureBrandMemory' && isDone && output?.success === true && (
        <button
          type="button"
          onClick={() => navigate('/brand?tab=guides')}
          className="mt-0.5 ml-5 flex items-center gap-1 text-[11px] text-paper-dim transition-colors hover:text-paper"
        >
          <BookmarkCheck size={10} className="flex-shrink-0" />
          <span>See it in Brand</span>
        </button>
      )}
      {skSteps && <ProcessDisclosure events={skSteps} />}
      {hasDetail && (
        <div
          className={`grid transition-all duration-300 ease-in-out ${
            isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Generated image"
                className="mt-1 ml-5 max-w-[calc(100%-1.25rem)] rounded-lg"
              />
            ) : (
              <div className="mt-1 ml-5 space-y-0.5 border-l border-line pl-2">
                {detailLines!.map((line, index) => (
                  <div key={index} className="text-[11px] text-paper-faint">
                    {line}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Section Context Parsing (for message bubbles) ──────────────────────

const CONTEXT_PREFIX_RE = /^\[Regarding the (.+?) section: "([\s\S]+?)"\]\n\n/

const LABEL_TO_SECTION_TYPE: Record<string, string> = {
  Header: 'header',
  Hero: 'hero',
  Text: 'text',
  Image: 'image',
  Button: 'cta',
  Divider: 'divider',
  Columns: 'columns',
}

function parseContextPrefix(text: string): {
  context: { label: string; preview: string; sectionType: string } | null
  rest: string
} {
  const match = text.match(CONTEXT_PREFIX_RE)
  if (!match) return { context: null, rest: text }
  const label = match[1]
  const preview = match[2]
  const sectionType = LABEL_TO_SECTION_TYPE[label] || 'text'
  return { context: { label, preview, sectionType }, rest: text.slice(match[0].length) }
}

// ─── Chat Message Bubble ─────────────────────────────────────────────────

/** True when a send was refused by the server's per-chat turn lock (409 chat_busy). */
function isChatBusyError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '')
  return msg.includes('chat_busy') || msg.includes('409')
}

function isToolPart(part: Record<string, unknown>): boolean {
  return (
    typeof part.type === 'string' &&
    (part.type as string).startsWith('tool-') &&
    part.type !== 'tool-result'
  )
}

function isReasoningPart(part: Record<string, unknown>): boolean {
  return part.type === 'reasoning'
}

export function ChatMessageBubble({
  message,
  onToolResult,
  hideToolIndicators = false,
  bubbleAssistant = false,
  onApprovalResponse,
}: {
  message: { id: string; role: string; parts: Record<string, unknown>[] }
  onToolResult?: (part: Record<string, unknown>) => void
  hideToolIndicators?: boolean
  /** Answers a paused tool-approval card (AI SDK v7 HITL); wired per chat instance. */
  onApprovalResponse?: (args: { id: string; approved: boolean }) => void
  /**
   * Wrap assistant text in a chat bubble (left-aligned, mirrors the user
   * bubble). Off by default — the composer renders assistant text bare as "the
   * document". The onboarding conversation turns it on so the short back-and-forth
   * reads as a chat. Reasoning/tool meta lines stay outside the bubble.
   */
  bubbleAssistant?: boolean
}) {
  const isUser = message.role === 'user'
  const processedRef = useRef<Set<string>>(new Set())

  // Process tool results to update creative fields
  useEffect(() => {
    if (!message.parts || !onToolResult) return
    for (const part of message.parts) {
      if (!isToolPart(part) || part.state !== 'output-available') continue
      const toolCallId = part.toolCallId as string
      if (processedRef.current.has(toolCallId)) continue
      processedRef.current.add(toolCallId)
      onToolResult(part)
    }
  }, [message.parts, onToolResult])

  // ── User message: purple bubble with optional image + context chip ──
  if (isUser) {
    const rawText = (message.parts || [])
      .filter(p => p.type === 'text')
      .map(p => p.text as string)
      .join('')

    const fileParts = (message.parts || [])
      .filter(
        p =>
          p.type === 'file' &&
          typeof p.mediaType === 'string' &&
          (p.mediaType as string).startsWith('image/') &&
          p.url
      )
      .map((p, i) => ({
        src: p.url as string,
        key: (p.attachmentId as string | undefined) || `img-${i}`,
      }))

    if (!rawText && fileParts.length === 0) return null

    const { context: parsedCtx, rest: textContent } = parseContextPrefix(rawText)

    return (
      <div className="mb-3 flex justify-end">
        <div className="max-w-[85%] overflow-hidden rounded-[12px] border border-line bg-bg-3 text-[13.5px] text-paper">
          {fileParts.length === 1 ? (
            <img
              src={fileParts[0].src}
              alt="Uploaded image"
              className="max-h-48 w-full object-cover"
            />
          ) : fileParts.length > 1 ? (
            <div className="grid grid-cols-2 gap-0.5">
              {fileParts.map((fp, i) => (
                <img
                  key={fp.key || `img-${i}`}
                  src={fp.src}
                  alt="Uploaded image"
                  className="aspect-square w-full object-cover"
                />
              ))}
            </div>
          ) : null}
          <div className="px-4 py-2.5">
            {parsedCtx && (
              <div className="mb-1.5">
                <SectionContextChip
                  context={{
                    sectionId: '',
                    sectionType: parsedCtx.sectionType as SectionContext['sectionType'],
                    label: parsedCtx.label,
                    preview: parsedCtx.preview,
                  }}
                />
              </div>
            )}
            {textContent && <span className="whitespace-pre-wrap">{textContent}</span>}
          </div>
        </div>
      </div>
    )
  }

  // ── Assistant message: sequential parts ──
  const parts = message.parts || []

  // Build a set of tool names that eventually succeeded, so we can hide earlier failures
  const succeededTools = new Set<string>()
  for (const p of parts) {
    if (isToolPart(p) && p.state === 'output-available') {
      const name =
        typeof p.toolName === 'string'
          ? p.toolName
          : typeof p.type === 'string' && (p.type as string).startsWith('tool-')
            ? (p.type as string).slice(5)
            : ''
      const pOutput = p.output as Record<string, unknown> | undefined
      const isSoftFail = pOutput?.success === false
      if (name && !isSoftFail) succeededTools.add(name)
    }
  }

  const hasVisibleContent = parts.some(
    p =>
      isReasoningPart(p) ||
      isToolPart(p) ||
      (p.type === 'text' && typeof p.text === 'string' && (p.text as string).trim())
  )
  if (!hasVisibleContent) return null

  return (
    <div className="mb-3 flex justify-start">
      <div className="max-w-[85%] space-y-0">
        {parts.map((part, i) => {
          // ── Reasoning / thinking ──
          if (isReasoningPart(part)) {
            return (
              <ThinkingBlock
                key={`r-${i}`}
                text={(part.text as string) || ''}
                isStreaming={part.state === 'streaming'}
              />
            )
          }

          // ── Tool calls (hide failed attempts when a retry succeeded) ──
          if (isToolPart(part)) {
            if (part.state === 'output-error') {
              const name =
                typeof part.toolName === 'string'
                  ? part.toolName
                  : typeof part.type === 'string' && (part.type as string).startsWith('tool-')
                    ? (part.type as string).slice(5)
                    : ''
              if (name && succeededTools.has(name)) return null
            }
            const receiptToolName =
              typeof part.toolName === 'string'
                ? part.toolName
                : typeof part.type === 'string' && (part.type as string).startsWith('tool-')
                  ? (part.type as string).slice(5)
                  : ''
            // HITL approval flow renders in every room, hidden indicators or not:
            // the card IS the confirmation UI, and a denial leaves a quiet trace.
            if (part.state === 'approval-requested' || part.state === 'approval-responded') {
              return (
                <ToolApprovalCard
                  key={`a-${part.toolCallId as string}`}
                  part={part}
                  onRespond={onApprovalResponse}
                />
              )
            }
            if (part.state === 'output-denied') {
              return (
                <div
                  key={`d-${part.toolCallId as string}`}
                  className="mb-1 px-2 py-1 text-[11px] text-paper-faint"
                >
                  Left it as scheduled. No changes made.
                </div>
              )
            }
            // Human-handoff receipt renders as a real card in EVERY room (both
            // indicator branches): the confirmation, or the honest failure, IS
            // the product moment here, not tool chrome.
            if (
              receiptToolName === 'escalateToSupport' &&
              part.state === 'output-available' &&
              part.output
            ) {
              const output = part.output as Record<string, unknown>
              return (
                <SupportHandoffCard
                  key={`h-${part.toolCallId as string}`}
                  status={(output.status as SupportHandoffStatus) || 'failed'}
                />
              )
            }
            // Render rich card for createSocialPost results (unless hidden — rendered externally)
            if (!hideToolIndicators) {
              const toolName = typeof part.toolName === 'string' ? part.toolName : ''
              if (
                toolName === 'createSocialPost' &&
                part.state === 'output-available' &&
                part.output
              ) {
                const output = part.output as Record<string, unknown>
                if (output.success && output.creativeId) {
                  const platform = (output.platform as string) || 'instagram'
                  return (
                    <CreativeGenerationCard
                      key={`card-${output.creativeId}`}
                      creativeId={output.creativeId as string}
                      type={platform}
                      name={(output.name as string) || 'Creative'}
                      initialStatus={
                        (output.status as 'generating' | 'created' | 'failed') || 'created'
                      }
                      projectId={output.projectId as string}
                      platform={platform}
                    />
                  )
                }
              }
              return (
                <ToolCallIndicator
                  key={`t-${part.toolCallId as string}`}
                  part={part}
                  live={!!onApprovalResponse}
                />
              )
            }
            // hideToolIndicators (assistant chat renders creation cards itself):
            // receipt tools still show — "Checked your calendar" is trust, not debug.
            if (isReceiptTool(receiptToolName)) {
              return (
                <ToolCallIndicator
                  key={`t-${part.toolCallId as string}`}
                  part={part}
                  live={!!onApprovalResponse}
                />
              )
            }
            return null
          }

          // ── First-creation process beats (persisted on the welcome message) ──
          // Renders the SAME collapsible "N steps" disclosure the live ladder showed, so
          // the creation process stays in the thread instead of vanishing on completion.
          if (
            part.type === 'data-socialkit-steps' &&
            Array.isArray((part as { steps?: unknown }).steps)
          ) {
            return (
              <ProcessDisclosure
                key={`steps-${i}`}
                events={(part as { steps: GenerationPhaseEvent[] }).steps}
              />
            )
          }

          // ── Step boundary ──
          if (part.type === 'step-start') {
            return null // clean separation handled by spacing
          }

          // ── Text content (rendered as markdown) ──
          // Default: assistant content reads as the document; transparent on
          // canvas with paper text. No bubble chrome — chat bubbles compete
          // with markdown rendering for the eye. `bubbleAssistant` opts into a
          // left-aligned bubble for conversational surfaces (onboarding).
          if (part.type === 'text' && typeof part.text === 'string' && part.text.trim()) {
            // The [[options: a | b | c]] marker is a UI contract (tappable
            // chips), never prose — strip it here so no room can leak it.
            const displayText = part.text.replace(OPTIONS_RE, '').trimEnd()
            if (!displayText.trim()) return null
            if (bubbleAssistant) {
              // Each blank-line-separated block becomes its own bubble, so a
              // single agent turn reads as a few quick chat messages.
              const blocks = displayText
                .split(/\n{2,}/)
                .map(b => b.trim())
                .filter(Boolean)
              return (
                <div key={`x-${i}`} className="space-y-1.5">
                  {blocks.map((block, bi) => (
                    <div
                      key={`x-${i}-${bi}`}
                      className="w-fit max-w-full rounded-[12px] rounded-tl-[3px] border border-line bg-bg-3 px-3.5 py-2.5 text-[13.5px] leading-[1.55] text-paper"
                    >
                      <MarkdownContent>{block}</MarkdownContent>
                    </div>
                  ))}
                </div>
              )
            }
            return (
              <div key={`x-${i}`} className="px-1 py-1.5 text-[13.5px] leading-[1.55] text-paper">
                <MarkdownContent>{displayText}</MarkdownContent>
              </div>
            )
          }

          return null
        })}
      </div>
    </div>
  )
}

// ─── Section Type Icons ───────────────────────────────────────────────────

const SECTION_TYPE_ICONS: Record<string, React.FC<{ size?: number; className?: string }>> = {
  text: Type,
  image: Image,
  cta: MousePointerClick,
  hero: Sparkles,
  header: Heading1,
  columns: LayoutGrid,
  caption: Type,
  hashtags: Hash,
}

// ─── Section Context Chip ──────────────────────────────────────────────────

function SectionContextChip({
  context,
  onDismiss,
}: {
  context: SectionContext
  onDismiss?: () => void
}) {
  const Icon = SECTION_TYPE_ICONS[context.sectionType]
  return (
    <div className="bg-acid-tint flex items-center gap-1.5 rounded-[6px] border border-acid px-2 py-1">
      {Icon && <Icon size={13} className="flex-shrink-0 text-acid" />}
      <span className="mono flex-shrink-0 text-[10px] tracking-[0.15em] text-acid uppercase">
        {context.label}
      </span>
      {context.preview && (
        <span className="min-w-0 flex-1 truncate text-[11px] text-paper-dim">
          {context.preview}
        </span>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="flex-shrink-0 rounded-[4px] p-0.5 text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
          aria-label="Remove section context"
        >
          <X size={12} />
        </button>
      )}
    </div>
  )
}

// ─── Chat Empty State ──────────────────────────────────────────────────────

const CHAT_CAPABILITIES: Record<CreativeType, string[]> = {
  [CreativeType.InstagramPost]: [
    'Write captions & hashtags',
    'Generate images with AI',
    'Create short videos',
  ],
  [CreativeType.FacebookPost]: [
    'Write engaging messages',
    'Generate images with AI',
    'Optimize for engagement',
  ],
  [CreativeType.TikTokVideo]: [
    'Write captions & hashtags',
    'Generate short videos',
    'Optimize for TikTok',
  ],
  [CreativeType.LinkedInPost]: [
    'Write professional post text',
    'Generate images & videos',
    'Add relevant hashtags',
  ],
  [CreativeType.XPost]: [
    'Write concise posts (280 chars)',
    'Generate images with AI',
    'Add relevant hashtags',
  ],
  [CreativeType.YouTubeShort]: [
    'Write titles & descriptions',
    'Generate short videos',
    'Add relevant hashtags',
  ],
  [CreativeType.PinterestPin]: [
    'Write titles & descriptions',
    'Generate pin images',
    'Add destination links',
  ],
}

// Bring-your-own-content: the user's media publishes untouched, so the empty
// state must not advertise image/video generation. Words-only help instead —
// same contract the composer enforces (see isUserMediaContent).
const BYO_CHAT_CAPABILITIES: string[] = [
  'Write me a better caption',
  'Suggest hashtags',
  'When should this go out?',
  'Make a Taka version of this post',
]

const CHAT_PLACEHOLDERS: Record<CreativeType, string> = {
  [CreativeType.InstagramPost]: 'Change the caption, image, tone...',
  [CreativeType.FacebookPost]: 'Change the message, image, tone...',
  [CreativeType.TikTokVideo]: 'Change the caption, video, hashtags...',
  [CreativeType.LinkedInPost]: 'Change the text, image, hashtags...',
  [CreativeType.XPost]: 'Change the text, image, hashtags...',
  [CreativeType.YouTubeShort]: 'Change the title, description, video...',
  [CreativeType.PinterestPin]: 'Change the title, description, image...',
}

function ChatEmptyState({
  creativeId,
  creativeType,
}: {
  creativeId: string
  creativeType: CreativeType
}) {
  const meta = creativeTypeMeta[creativeType]
  // BYO post → words-only suggestions; Taka never touches the user's media.
  const isByoPost = useCreativeStore(s =>
    isUserMediaContent(
      s.creatives.find(c => c.id === creativeId)?.content as
        Record<string, unknown> | null | undefined
    )
  )
  const capabilities = isByoPost ? BYO_CHAT_CAPABILITIES : CHAT_CAPABILITIES[creativeType]
  const snapshot = useCreativeProcessStore(s => s.byCreative[creativeId]?.snapshot)

  // When the freshly-opened post is BLOCKED by a hard gate and the judge has a concrete
  // fix, lead with that instead of a generic invite — the score card is calm/collapsed by
  // default, so the chat opener is where the blocker becomes discoverable + one tap to fix.
  const blocked = !!snapshot?.verdict && !isShipVerdict(snapshot.verdict) && !!snapshot.singleFix

  if (blocked && snapshot?.singleFix) {
    return (
      <div className="flex h-full flex-col px-1 py-2">
        <div className="mb-4 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-acid">
            <Sparkles size={18} />
          </div>
          <p className="text-[14px] font-medium text-paper">
            {verdictHeadline(String(snapshot.verdict))}
          </p>
          <p className="mt-1 text-[12px] text-paper-faint">
            Here's the highest-leverage fix. I can apply it for you.
          </p>
        </div>

        <FixWithTakaCard creativeId={creativeId} singleFix={snapshot.singleFix} className="mb-4" />

        <p className="text-center text-[11px] text-paper-faint">
          …or tell me anything else to change.
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col px-1 py-2">
      {/* Hero — matches AssistantChat empty state recipe so the two
          chat surfaces feel like the same product. */}
      <div className="mb-5 text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-acid">
          <Sparkles size={18} />
        </div>
        <p className="text-[14px] font-medium text-paper">Edit your {meta.label.toLowerCase()}</p>
        <p className="mt-1 text-[12px] text-paper-faint">
          Tell me what to change. I'll update the preview live.
        </p>
      </div>

      {/* Capabilities */}
      <div className="mb-5">
        <p className="mono mb-2 text-[10px] tracking-[0.2em] text-paper-faint uppercase">
          I can help with
        </p>
        <div className="space-y-1">
          {capabilities.map(cap => (
            <div
              key={cap}
              className="flex items-center gap-2 rounded-[8px] px-2 py-1.5 text-[12px] text-paper-dim"
            >
              <Zap size={13} className="flex-shrink-0 text-paper-faint" />
              <span>{cap}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Side Chat ────────────────────────────────────────────────────────────

interface SideChatProps {
  creativeId: string
  creativeType: CreativeType
  showHeader?: boolean
  projectId?: string
}

function restoreMessages(data: {
  messages?: Array<{ id: string; role: string; parts: unknown[] }>
}): UIMessage[] {
  return (data.messages ?? []).map(m => ({ id: m.id, role: m.role, parts: m.parts }) as UIMessage)
}

export function SideChat({
  creativeId,
  creativeType,
  showHeader = true,
  projectId,
}: SideChatProps) {
  const [initialMessages, setInitialMessages] = useState<UIMessage[] | undefined>(undefined)
  const [messagesLoaded, setMessagesLoaded] = useState(false)

  const chatKey = projectId || creativeId

  useEffect(() => {
    setMessagesLoaded(false)
    setInitialMessages(undefined)

    let cancelled = false

    if (projectId) {
      // Project-level chat: the project payload carries its chat. Generated
      // service, statically imported: a runtime import() here would split the
      // client into a chunk that 404s on a pre-deploy tab (see stale-bundle.ts).
      apiCall(ProjectService.projectControllerFindOneV1, null, projectId)
        .then(data => {
          if (cancelled) return
          if (data?.projectChat?.messages?.length) {
            setInitialMessages(restoreMessages(data.projectChat))
          }
          setMessagesLoaded(true)
        })
        .catch(err => {
          console.error(`SideChat: failed to load project chat for ${projectId}:`, err)
          if (!cancelled) setMessagesLoaded(true)
        })
    } else {
      apiCall(CreativeService.creativeControllerGetActiveChatV1, null, creativeId)
        .then(data => {
          if (cancelled) return
          if (data?.messages?.length) {
            setInitialMessages(restoreMessages(data))
          }
          setMessagesLoaded(true)
        })
        .catch(err => {
          console.error(`SideChat: failed to load active chat for creative ${creativeId}:`, err)
          if (!cancelled) setMessagesLoaded(true)
        })
    }

    return () => {
      cancelled = true
    }
    // chatKey gates reload; projectId/creativeId are read from closure when the key changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: omit creativeId so platform tab switches don't remount chat
  }, [chatKey])

  if (!messagesLoaded) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-bg-2">
        <Loader2 size={20} className="animate-spin text-paper-dim" />
      </div>
    )
  }

  return (
    <SideChatInner
      key={chatKey}
      creativeId={creativeId}
      creativeType={creativeType}
      showHeader={showHeader}
      initialMessages={initialMessages}
      projectId={projectId}
    />
  )
}

function SideChatInner({
  creativeId,
  creativeType,
  showHeader = true,
  initialMessages,
  projectId,
}: SideChatProps & { initialMessages?: UIMessage[] }) {
  const { track } = useAnalytics()
  const { gate } = useSubscriptionGate()
  // View-only when the creative is live — disables the input/send/attach so a
  // published post can't be edited from chat. Set by ComposerReadOnlyProvider.
  const readOnly = useComposerReadOnly()
  const totalCredits = useAppSelector(selectTotalCredits)
  // Out of credits blocks the composer entirely: text + media both need credits
  // here, so we replace the input with a banner rather than let the user type
  // into a dead end. Live/read-only posts keep their own read-only treatment.
  const outOfCredits = useAppSelector(selectIsOutOfCredits) && !readOnly
  // Every content write in this component applies an AI TOOL RESULT the server
  // already persisted (persist-tool-output.ts for the classic tools, the SocialKit
  // generation service for revise/generate/restore). That is SERVER state, so it
  // goes through the state-only path. Routing it through updateCreativeContent,
  // even flagged as a non-user edit, scheduled a debounced PUT of the FULL
  // creative: for a carousel, whose authoritative creative:content:updated is
  // always truncated, that PUT echoed the stale caption back over the AI's
  // rewrite within a second of it landing. Only setImage is client-owned.
  const applyServerContent = useCreativeStore(s => s.applyServerContentPatch)
  const applyServerCreativePatch = useCreativeStore(s => s.applyServerCreativePatch)
  const updateCreativeContentRaw = useCreativeStore(s => s.updateCreativeContent)
  const persistClientOwnedContent = useCallback(
    (id: string, fields: Record<string, unknown>) =>
      updateCreativeContentRaw(id, fields, { userEdit: false }),
    [updateCreativeContentRaw]
  )
  // When this post has a live canvas, flush pending canvas autosaves before a chat
  // turn so an AI revise merges the user's latest edits (not a stale snapshot). Null
  // when there's no editable composition. See CompositionEditorProvider.flushSave.
  const composerEditor = useComposerEditor()
  const setGeneratingFields = useCreativeStore(s => s.setGeneratingFields)
  const clearGenerating = useCreativeStore(s => s.clearGenerating)
  const consumePrompt = useInitialPromptStore(s => s.consumePrompt)
  const creatorMode = CREATOR_MODE_MAP[creativeType]

  // ─── First-generation Pusher tracking ──────────────────────────────────
  // Mirrors what EditPanel used to do. Fires COMPOSER_CONTENT_GENERATED
  // exactly once per creative open — the first time the server streams a
  // content update for this creative.id.
  const contentGeneratedRef = useRef(false)
  const handlePusherContentUpdate = useCallback(
    (data: { creativeId: string; fields: Record<string, unknown> }) => {
      if (data.creativeId !== creativeId) return
      if (contentGeneratedRef.current) return
      contentGeneratedRef.current = true
      track(AnalyticsEvents.Composer.COMPOSER_CONTENT_GENERATED, {
        creative_id: creativeId,
        creative_type: creativeType,
      })
    },
    [creativeId, creativeType, track]
  )
  useListenToPusherEvent<{ creativeId: string; fields: Record<string, unknown> }>(
    PusherEvent.CreativeContentUpdated,
    handlePusherContentUpdate
  )

  // ─── Image upload ──────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)
  const { pending, addFiles, removeAt, clear, hasUploading, ready } = usePendingAttachments()

  /** Wraps addFiles with analytics tracking per file */
  const addFilesWithTracking = useCallback(
    (files: File[], uploadMethod: 'button' | 'paste' | 'drag') => {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue
        track(AnalyticsEvents.Composer.COMPOSER_IMAGE_UPLOADED, {
          creative_id: creativeId,
          creative_type: creativeType,
          upload_method: uploadMethod,
        })
      }
      addFiles(files)
    },
    [addFiles, track, creativeId, creativeType]
  )

  // Clipboard paste support — usePasteImage already invokes per-file
  usePasteImage(
    useCallback((file: File) => addFilesWithTracking([file], 'paste'), [addFilesWithTracking])
  )

  // Ref to track attachment IDs for the current message (used by transport body)
  const pendingAttachmentIdsRef = useRef<string[]>([])

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${API_BASE}/v1/creative/chat`,
        headers: () => ({
          ...(OpenAPI.TOKEN ? { Authorization: `Bearer ${OpenAPI.TOKEN}` } : {}),
        }),
        body: () => {
          if (projectId) {
            // Project-scoped chat (the composer). The chat history is keyed by
            // project, but tools act on the ACTIVE platform's creative, so pass
            // its id + content too — the SocialKit revise/generate tools need it.
            const active = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
            return {
              projectId,
              creatorMode,
              ...(creativeId ? { creativeId } : {}),
              ...(active?.content ? { creativeContent: active.content } : {}),
              ...(pendingAttachmentIdsRef.current.length > 0
                ? { attachmentIds: pendingAttachmentIdsRef.current }
                : {}),
            }
          }
          const creative = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
          return {
            creatorMode,
            creativeId: creativeId,
            creativeName: creative?.name,
            creativeContent: creative?.content,
            ...(pendingAttachmentIdsRef.current.length > 0
              ? { attachmentIds: pendingAttachmentIdsRef.current }
              : {}),
          }
        },
      }),
    [creatorMode, creativeId, projectId]
  )

  const {
    messages,
    sendMessage,
    setMessages,
    addToolOutput,
    addToolApprovalResponse,
    status,
    error: chatError,
  } = useChat({
    id: projectId ? `project-${projectId}` : `creative-${creativeId}`,
    transport,
    generateId: ulid,
    // HITL: once the user answers an approval card, resubmit automatically so
    // the paused tool executes (or is denied) without an extra send.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    ...(initialMessages?.length ? { messages: initialMessages } : {}),
  })

  // Track restored message IDs so we don't replay their tool results
  // (replaying would overwrite manual edits the user made in the editor)
  const initialMessageIds = useMemo(
    () => new Set(initialMessages?.map(m => m.id) || []),
    [initialMessages]
  )

  // ─── Inject missed Pusher messages when returning to a creative ──────
  const pendingQueue = useChatStatusStore(s => s.pendingMessages.get(creativeId))

  // Keep fresh references in refs to avoid stale closures in the effect
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const statusRef = useRef<string>(status)
  statusRef.current = status

  useEffect(() => {
    if (!pendingQueue?.length) return
    // Never touch useChat's message list mid-stream — draining resumes on the
    // status transition to idle (status is a dep), so a tab that was busy with
    // its own turn catches up the moment it finishes instead of only when the
    // user navigates back to the creative.
    if (statusRef.current === 'streaming' || statusRef.current === 'submitted') return

    const drained = useChatStatusStore.getState().consumePendingMessages(creativeId)
    if (drained.length === 0) return

    const currentMessages = messagesRef.current
    const toAppend: UIMessage[] = []
    for (const pending of drained) {
      const existingMsg = currentMessages.find(m => m.id === pending.id)
      if (existingMsg) {
        // Reconcile stuck tool parts: if the SSE stream dropped mid-generation,
        // tool parts may be stuck at 'call' state. The pending message from the
        // server has the resolved states — use addToolOutput to fix them.
        for (const pp of pending.parts as Record<string, unknown>[]) {
          if (!isToolPart(pp) || (pp.state !== 'output-available' && pp.state !== 'output-error'))
            continue
          const ep = (existingMsg.parts as Record<string, unknown>[]).find(
            p => isToolPart(p) && p.toolCallId === pp.toolCallId
          )
          if (ep && ep.state !== 'output-available' && ep.state !== 'output-error') {
            addToolOutput(
              pp.state === 'output-available'
                ? {
                    tool: pp.toolName as string,
                    toolCallId: pp.toolCallId as string,
                    output: pp.output,
                  }
                : {
                    state: 'output-error',
                    tool: pp.toolName as string,
                    toolCallId: pp.toolCallId as string,
                    errorText: (pp.errorText as string) ?? 'Tool failed',
                  }
            )
          }
        }
        continue
      }
      toAppend.push({
        id: pending.id,
        role: pending.role as 'assistant',
        parts: pending.parts as UIMessage['parts'],
      })
    }

    if (toAppend.length > 0) {
      setMessages([...currentMessages, ...toAppend])
    }

    // Background re-fetch from server to get full history (anything Pusher
    // dropped entirely, e.g. before this tab subscribed)
    const timer = setTimeout(async () => {
      // Skip if the user has started a new interaction
      if (statusRef.current === 'streaming' || statusRef.current === 'submitted') return

      try {
        const data = await apiCall(
          CreativeService.creativeControllerGetActiveChatV1,
          null,
          creativeId
        )
        if (data?.messages?.length) {
          // Re-check: don't overwrite if user started interacting during the fetch
          if (statusRef.current === 'streaming' || statusRef.current === 'submitted') return

          setMessages(restoreMessages(data))
        }
      } catch (err) {
        // The immediately-injected messages are good enough; surface the failure
        // so the console bridge fires for visibility into recurring issues.
        console.error(`SideChat: pending-message refetch failed for creative ${creativeId}:`, err)
      }
    }, 2000)

    return () => clearTimeout(timer)
  }, [pendingQueue, creativeId, setMessages, addToolOutput, status])

  const [input, setInput] = useState('')
  const [emptyResponseMsgId, setEmptyResponseMsgId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const isLoading = status === 'streaming' || status === 'submitted'
  // Another tab/device is mid-turn on this creative (their typing event). A
  // send now would 409 on the server's turn lock — queue it instead, same as
  // when this tab is busy.
  const crossTabBusy = useChatStatusStore(s => s.typingCreatives.has(creativeId))

  // ─── Busy-send queue: nothing typed while Taka works is ever lost ───
  // Messages submitted while a turn is running (ours or another tab's) wait
  // here, render as dimmed bubbles, and auto-send sequentially when free.
  const [queuedSends, setQueuedSends] = useState<Array<{ id: string; text: string }>>([])
  const drainingRef = useRef(false)

  useEffect(() => {
    if (isLoading || crossTabBusy || drainingRef.current) return
    if (queuedSends.length === 0) return
    const [head, ...rest] = queuedSends
    drainingRef.current = true
    setQueuedSends(rest)
    sendMessage({ text: head.text })
      .catch(err => {
        // A turn-lock 409 (another tab won the race) is expected — requeue
        // quietly; the busy state that follows keeps it parked until free.
        if (!isChatBusyError(err)) {
          console.error('[SideChat] queued send failed, requeueing:', err)
        }
        setQueuedSends(q => [head, ...q])
      })
      .finally(() => {
        // sendMessage resolves when the TURN completes, so clearing here makes
        // the drain naturally sequential (one queued message per turn).
        drainingRef.current = false
      })
  }, [isLoading, crossTabBusy, queuedSends, sendMessage])

  useEffect(() => {
    if (!chatError) return
    const msg = chatError instanceof Error ? chatError.message : String(chatError)
    console.error('[SideChat] sendMessage error:', msg)
  }, [chatError])

  // Track whether AI has already renamed this creative (allow only once per session)
  const hasRenamedRef = useRef(false)

  // ─── Per-field loading: shimmer only the sections being generated ──────
  const isFirstGenRef = useRef(false)

  // Detect first generation vs edit when streaming starts
  useEffect(() => {
    if (isLoading) {
      const creative = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
      const c = creative?.content as Record<string, unknown> | undefined
      // Check if all content fields are empty (first generation)
      if (!c) {
        isFirstGenRef.current = true
      } else {
        const values = Object.values(c)
        isFirstGenRef.current = values.every(v => !v || (Array.isArray(v) && v.length === 0))
      }
    }
  }, [isLoading, creativeId])

  const setTyping = useChatStatusStore(s => s.setTyping)

  // Sync generating fields to store so previews can shimmer per-section
  useEffect(() => {
    if (!isLoading) {
      clearGenerating(creativeId)
      return
    }

    // First generation: shimmer everything — use all known fields for this creative type
    if (isFirstGenRef.current) {
      const creative = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
      const c = creative?.content as Record<string, unknown> | undefined
      const fields = c ? Object.keys(c) : []
      setGeneratingFields(creativeId, new Set(fields.length > 0 ? fields : ['subject', 'body']))
      return
    }

    // Subsequent edit: derive fields from in-flight tool calls.
    // AI SDK v6: part.type is "tool-{name}", properties are flat.
    const inFlightFields = new Set<string>()
    for (const msg of messages) {
      if (msg.role !== 'assistant') continue
      for (const part of (msg as { parts: Record<string, unknown>[] }).parts) {
        if (
          isToolPart(part) &&
          part.state !== 'output-available' &&
          part.state !== 'output-error'
        ) {
          const toolName = (part.type as string).slice(5)
          const input = part.input as Record<string, unknown> | undefined
          const fields = getFieldsFromToolInvocation(toolName, input)
          for (const f of fields) inFlightFields.add(f)
        }
      }
    }

    if (inFlightFields.size > 0) {
      setGeneratingFields(creativeId, inFlightFields)
    }
  }, [isLoading, messages, creativeId, clearGenerating, setGeneratingFields])

  // Auto-scroll to bottom (instant on initial load, smooth for new messages)
  const hasScrolledRef = useRef(false)
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: hasScrolledRef.current ? 'smooth' : 'auto' })
    hasScrolledRef.current = true
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Send initial prompt if present (from HomePage create flow)
  // Skip if we restored messages from a previous session
  const consumeAttachment = useInitialPromptStore(s => s.consumeAttachment)
  const initialPromptSent = useRef(false)
  useEffect(() => {
    if (initialPromptSent.current) return
    if (initialMessages?.length) return // conversation already has history
    const prompt = consumePrompt(creativeId)
    const attachment = consumeAttachment(creativeId)
    if (prompt) {
      initialPromptSent.current = true
      if (attachment) {
        pendingAttachmentIdsRef.current = [attachment.attachmentId]
        sendMessage({
          parts: [
            {
              type: 'file',
              mediaType: attachment.mediaType,
              url: attachment.dataUrl,
              attachmentId: attachment.attachmentId,
            },
            { type: 'text', text: prompt },
          ],
        } as Parameters<typeof sendMessage>[0]).finally(() => {
          pendingAttachmentIdsRef.current = []
        })
      } else {
        sendMessage({ text: prompt })
      }
    }
  }, [creativeId, consumePrompt, consumeAttachment, sendMessage, initialMessages])

  // Consume queued prompts (from external triggers like the mobile Generate button)
  const consumeQueuedPrompt = useQueuedPromptStore(s => s.consumePrompt)
  const queuedText = useQueuedPromptStore(s => s.prompts[creativeId])

  useEffect(() => {
    if (!queuedText || isLoading) return
    const prompt = consumeQueuedPrompt(creativeId)
    if (prompt) {
      sendMessage({ text: prompt })
    }
  }, [queuedText, isLoading, creativeId, consumeQueuedPrompt, sendMessage])

  // Section context (from preview "Send to Chat" button)
  const sectionContext = useSectionContextStore(s => s.contexts[creativeId])
  const clearSectionContext = useSectionContextStore(s => s.clearContext)
  const consumeSectionContext = useSectionContextStore(s => s.consumeContext)

  // Consume chat prefills (from preview "Change..." actions) — sets input without sending
  const prefillText = useChatPrefillStore(s => s.prefills[creativeId])
  const consumePrefill = useChatPrefillStore(s => s.consumePrefill)

  useEffect(() => {
    if (!prefillText) return
    const text = consumePrefill(creativeId)
    if (text) {
      setInput(text)
    }
  }, [prefillText, creativeId, consumePrefill, setInput])

  // Focus input when section context arrives
  useEffect(() => {
    if (sectionContext) {
      inputRef.current?.focus()
    }
  }, [sectionContext])

  const handleToolResult = useCallback(
    (part: Record<string, unknown>) => {
      const output = part.output as Record<string, unknown> | undefined
      if (!output?.type) return

      // A successful AI revise on the post's CANONICAL bumps the local
      // contentRevision mirror, so pinned variants flip to "Customized ·
      // post changed" live instead of waiting for the next refetch.
      if (
        (output.type === 'revisePost' ||
          output.type === 'reviseCarousel' ||
          output.type === 'reviseReel' ||
          output.type === 'restoreVersion') &&
        output.success !== false
      ) {
        const creative = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
        if (creative?.projectId) {
          const ideaStore = useIdeaStore.getState()
          const idea = ideaStore.ideas.find(i => i.projectId === creative.projectId)
          if (idea?.primaryCreative?.id === creativeId) {
            ideaStore.noteCanonicalEditLocally(creative.projectId)
          }
        }
      }

      // Apply tool results to creative content
      switch (output.type) {
        case 'updateCaption':
          if (output.caption) {
            applyServerContent(creativeId, { caption: output.caption })
          }
          break
        case 'updateHashtags':
          if (output.hashtags) {
            applyServerContent(creativeId, { hashtags: output.hashtags })
          }
          break
        case 'updateImageDescription':
          if (output.description) {
            applyServerContent(creativeId, { imagePrompt: output.description })
          }
          break
        case 'updateContent':
          if (output.fields && typeof output.fields === 'object') {
            applyServerContent(creativeId, output.fields as Record<string, unknown>)
          }
          break
        case 'generateImage':
          if (output.attachmentId) {
            applyServerContent(creativeId, {
              imageAttachmentId: output.attachmentId as string,
              imageUrl: output.imageUrl as string,
            })
          }
          break
        // setImage is the one tool result the server does NOT persist
        // (persist-tool-output.ts: "handled client-side only"), so this write
        // must still reach the server, minus the "user edit" signal.
        case 'setImage':
          if (output.attachmentId) {
            persistClientOwnedContent(creativeId, {
              imageAttachmentId: output.attachmentId as string,
              imageUrl: output.imageUrl as string,
            })
          }
          break
        case 'editCarouselSlide': {
          if (!output.success || !output.mediaItem || typeof output.slideIndex !== 'number') break
          const current = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
          const existing =
            (current?.content as { mediaItems?: InstagramMediaItem[] } | undefined)?.mediaItems ??
            []
          const patch = applyEditedCarouselSlide(existing, {
            slideIndex: output.slideIndex,
            replacedAttachmentId: output.replacedAttachmentId as string | undefined,
            mediaItem: output.mediaItem as InstagramMediaItem,
          })
          if (patch) applyServerContent(creativeId, { ...patch })
          break
        }
        case 'generateInstagramCarousel': {
          if (!output.success) break
          const patch = buildCarouselContentPatch(output.mediaItems as InstagramMediaItem[])
          if (patch) applyServerContent(creativeId, { ...patch })
          break
        }
        // SocialKit static revise/regenerate/generate: patch the new master + copy
        // optimistically. Besides the instant preview, the imageAttachmentId change is what
        // triggers the composition editor's post-revise refetch — without this, a dropped or
        // misrouted creative:content:updated Pusher event leaves the open canvas permanently
        // stale (the "chat said it removed the layer but the preview didn't change" bug).
        case 'revisePost':
        case 'regenerateImage':
        case 'generatePost': {
          if (!output.success) break
          const patch: Record<string, unknown> = {}
          if (output.caption) patch.caption = output.caption
          if (output.hashtags) patch.hashtags = output.hashtags
          if (output.attachmentId) {
            patch.imageAttachmentId = output.attachmentId
            if (output.imageUrl) patch.imageUrl = output.imageUrl
          }
          if (Object.keys(patch).length) applyServerContent(creativeId, patch)
          break
        }
        // SocialKit carousel revise: apply the re-rendered deck AND the rewritten
        // caption/hashtags. This tool result is the only full copy of the new deck
        // the client receives: the authoritative creative:content:updated for a deck
        // is always truncated (mediaItems blow the Pusher cap) and its recovery is a
        // refetch (ChatPusherHandler). A caption-only revise ships no new slides, so
        // mediaItems alone would leave the new caption off the screen.
        case 'reviseCarousel': {
          if (!output.success) break
          const patch: Record<string, unknown> = {}
          if (Array.isArray(output.mediaItems)) {
            Object.assign(
              patch,
              buildCarouselContentPatch(output.mediaItems as InstagramMediaItem[]) ?? {}
            )
          }
          if (typeof output.caption === 'string') patch.caption = output.caption
          if (Array.isArray(output.hashtags)) patch.hashtags = output.hashtags
          if (Object.keys(patch).length) applyServerContent(creativeId, patch)
          break
        }
        // Version restore: apply the restored deck (or master) + caption/hashtags
        // optimistically; the server's creative:content:updated is the authoritative refresh.
        case 'restoreVersion': {
          if (!output.success) break
          const patch: Record<string, unknown> = {}
          if (Array.isArray(output.mediaItems)) {
            Object.assign(
              patch,
              buildCarouselContentPatch(output.mediaItems as InstagramMediaItem[]) ?? {}
            )
          } else if (output.attachmentId) {
            patch.imageAttachmentId = output.attachmentId
            if (output.imageUrl) patch.imageUrl = output.imageUrl
          }
          if (typeof output.caption === 'string') patch.caption = output.caption
          if (Array.isArray(output.hashtags)) patch.hashtags = output.hashtags
          if (Object.keys(patch).length) applyServerContent(creativeId, patch)
          break
        }
        case 'generateVideo':
          if (output.success && output.attachmentId) {
            applyServerContent(creativeId, {
              videoAttachmentId: output.attachmentId as string,
              videoUrl: output.videoUrl as string,
              videoStatus: 'ready',
              format: 'reel',
            })
          } else if (!output.success) {
            applyServerContent(creativeId, {
              videoStatus: 'failed',
            })
          }
          break
        // SocialKit reel revise: swap in the re-rendered mp4 optimistically (the server
        // also fires creative:content:updated as the authoritative refresh). Apply the
        // fresh poster too so the opening frame doesn't lag the new clip in the window
        // before the authoritative event lands. A failed/no_spec revise leaves the
        // existing clip untouched (the guard below).
        case 'reviseReel':
          if (output.success && output.attachmentId) {
            applyServerContent(creativeId, {
              videoAttachmentId: output.attachmentId as string,
              videoUrl: output.videoUrl as string,
              videoStatus: 'ready',
              format: 'reel',
              ...(output.posterUrl ? { imageUrl: output.posterUrl as string } : {}),
            })
          }
          break
        // SocialKit reel generate: swap the freshly rendered mp4 + poster into the composer
        // optimistically (the server also fires creative:content:updated authoritatively).
        case 'generateReel':
          if (output.success && output.attachmentId) {
            applyServerContent(creativeId, {
              videoAttachmentId: output.attachmentId as string,
              videoUrl: output.videoUrl as string,
              videoStatus: 'ready',
              format: 'reel',
              ...(output.posterUrl ? { imageUrl: output.posterUrl as string } : {}),
            })
          }
          break
        // The server already persisted the name (renameInternal). updateCreative
        // would echo the whole creative back as a PUT, stale content included.
        case 'renameCreative':
          if (output.name && !hasRenamedRef.current) {
            hasRenamedRef.current = true
            applyServerCreativePatch(creativeId, { name: output.name as string })
          }
          break
      }
    },
    [creativeId, applyServerContent, applyServerCreativePatch, persistClientOwnedContent]
  )

  const handleRetry = useCallback(() => {
    setEmptyResponseMsgId(null)
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user')
    if (!lastUserMsg) return
    const textPart = ((lastUserMsg as { parts: Record<string, unknown>[] }).parts || []).find(
      p => p.type === 'text'
    )
    const text = (textPart?.text as string) || ''
    if (text) {
      sendMessage({ text })
    }
  }, [messages, sendMessage])

  // Track user message count for is_first_message
  const userMessageCountRef = useRef(initialMessages?.filter(m => m.role === 'user').length ?? 0)

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault()
      if (readOnly) return
      const trimmed = input.trim()

      // Snapshot ready attachments to avoid race during async send
      const attachmentsToSend = ready.map(p => ({
        attachmentId: p.attachmentId!,
        mediaType: p.file.type,
        publicUrl: p.publicUrl!,
      }))

      if (!trimmed && attachmentsToSend.length === 0) return

      // Block send while uploads are still in flight
      if (hasUploading) return

      if (!gate('send_message')) return

      // Busy (this tab streaming, or another tab holds the turn): queue text
      // sends instead of silently dropping them — the old early-return made
      // the chat a black hole for the minutes a SocialKit revise can run.
      // Attachment sends keep the old behavior (the toolbar is disabled).
      if ((isLoading || crossTabBusy) && attachmentsToSend.length === 0) {
        const ctx = consumeSectionContext(creativeId)
        const queuedText = ctx
          ? `[Regarding the ${ctx.label} section: "${ctx.preview}"]\n\n${trimmed}`
          : trimmed
        track(AnalyticsEvents.Composer.COMPOSER_MESSAGE_SENT, {
          creative_id: creativeId,
          creative_type: creativeType,
          is_first_message: userMessageCountRef.current === 0,
          attachment_count: 0,
        })
        userMessageCountRef.current += 1
        setQueuedSends(q => [...q, { id: ulid(), text: queuedText }])
        setInput('')
        if (inputRef.current) inputRef.current.style.height = 'auto'
        return
      }
      if (isLoading) return

      // Chat and text are free. Never block a send on credit balance; if the
      // user asks for media at 0 credits, the media tool returns
      // insufficient_credits and the assistant relays it conversationally.
      setEmptyResponseMsgId(null)

      // Consume section context and prepend it to the message
      const ctx = consumeSectionContext(creativeId)
      const messageText = ctx
        ? `[Regarding the ${ctx.label} section: "${ctx.preview}"]\n\n${trimmed}`
        : trimmed

      track(AnalyticsEvents.Composer.COMPOSER_MESSAGE_SENT, {
        creative_id: creativeId,
        creative_type: creativeType,
        is_first_message: userMessageCountRef.current === 0,
        attachment_count: attachmentsToSend.length,
      })
      userMessageCountRef.current += 1

      // Set attachment IDs for the transport body before sending
      pendingAttachmentIdsRef.current = attachmentsToSend.map(a => a.attachmentId)

      // Build message parts. Send `url: publicUrl` (compact, real http URL)
      // instead of a multi-MB `data:` payload. The server's existing flow
      // strips `attachmentId` before the LLM call (SDK rejects unknown keys)
      // and persists file parts as `{ type: 'file', mediaType, attachmentId }`
      // via `toSyncPayload`; on history reload it rehydrates a fresh signed
      // url from the AttachmentEntity.
      const parts: Record<string, unknown>[] = attachmentsToSend.map(a => ({
        type: 'file',
        mediaType: a.mediaType,
        url: a.publicUrl,
        attachmentId: a.attachmentId,
      }))
      if (messageText) {
        parts.push({ type: 'text', text: messageText })
      }

      setInput('')
      clear()
      if (inputRef.current) inputRef.current.style.height = 'auto'

      // Flush any debounced canvas autosave NOW and AWAIT the PUT so the server-side revise
      // merges the user's latest layout/style edits rather than a snapshot that's still
      // in-flight (the PUT must land before the revise reads the stored composition).
      await composerEditor?.flushSave()

      try {
        if (parts.length === 1 && parts[0].type === 'text') {
          await sendMessage({ text: messageText })
        } else {
          await sendMessage({ parts } as Parameters<typeof sendMessage>[0])
        }
      } catch (err) {
        if (isChatBusyError(err) && attachmentsToSend.length === 0) {
          // Lost the turn-lock race to another tab between our busy check and
          // the POST — park the message in the queue instead of erroring.
          setQueuedSends(q => [...q, { id: ulid(), text: messageText }])
        } else {
          setInput(trimmed)
          console.error('[SideChat] handleSubmit error:', err)
        }
      } finally {
        pendingAttachmentIdsRef.current = []
      }
    },
    [
      input,
      isLoading,
      crossTabBusy,
      ready,
      hasUploading,
      clear,
      sendMessage,
      track,
      creativeId,
      creativeType,
      consumeSectionContext,
      gate,
      readOnly,
      composerEditor,
    ]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  // Track AI message received when streaming completes + detect empty responses
  const prevLoadingRef = useRef(isLoading)
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      // Streaming just finished — clear global typing immediately so
      // there's no flash between SSE end and the Pusher typing:ended event.
      setTyping(creativeId, false)

      // Check ALL new assistant messages (not just the last) to avoid false-positive
      // empty-response errors in multi-step tool-use flows where the final turn
      // may only contain reasoning parts.
      const newAssistantMsgs = messages.filter(
        m => m.role === 'assistant' && !initialMessageIds.has(m.id)
      )
      const lastAssistantMsg = newAssistantMsgs[newAssistantMsgs.length - 1]

      if (lastAssistantMsg) {
        const allParts = newAssistantMsgs.flatMap(
          m => (m as { parts: Record<string, unknown>[] }).parts || []
        )
        const hasToolCalls = allParts.some(
          p => typeof p.type === 'string' && (p.type as string).startsWith('tool-')
        )
        const hasText = allParts.some(
          p => p.type === 'text' && typeof p.text === 'string' && (p.text as string).trim()
        )
        // Credits are only spent on media. Gate the consume event on the
        // media-producing tool parts so free text/chat turns don't fire it.
        const hasMediaToolCall = allParts.some(
          p =>
            p.type === 'tool-generateImage' ||
            p.type === 'tool-generateInstagramCarousel' ||
            p.type === 'tool-editCarouselSlide' ||
            p.type === 'tool-generateVideo'
        )
        track(AnalyticsEvents.Composer.COMPOSER_MESSAGE_RECEIVED, {
          creative_id: creativeId,
          creative_type: creativeType,
          has_tool_calls: hasToolCalls,
        })

        if (hasMediaToolCall) {
          track(AnalyticsEvents.Credits.CREDIT_CONSUMED, {
            creative_type: creativeType,
            creative_id: creativeId,
            credits_remaining: totalCredits,
            consumed_from: 'subscription',
          })
        }

        if (!hasToolCalls && !hasText) {
          setEmptyResponseMsgId(lastAssistantMsg.id)

          const diag = buildEmptyResponseDiagnostics(
            messages,
            newAssistantMsgs,
            allParts,
            lastAssistantMsg
          )
          console.warn(
            `[EMPTY_RESPONSE] Creative: ${creativeId} (${creativeType})\n` +
              `  Messages: ${diag.totalMessageCount}, New assistant: ${diag.newAssistantMessageCount}\n` +
              `  Last parts: ${diag.lastPartTypes}, All parts: ${diag.allPartTypes}`
          )
          track(AnalyticsEvents.Composer.COMPOSER_EMPTY_RESPONSE, {
            creative_id: creativeId,
            creative_type: creativeType,
            total_message_count: diag.totalMessageCount,
            new_assistant_message_count: diag.newAssistantMessageCount,
            last_message_part_types: diag.lastPartTypes,
            all_new_part_types: diag.allPartTypes,
          })
        }
      } else {
        // A fully EMPTY completion (SSE: start → finish with zero content parts) never
        // materializes an assistant message at all, so the branch above can't see it —
        // the user's message would just sit there with no reply, no error, no retry.
        // Detect it by the turn ending while the tail of the thread is still the user.
        const tail = messages[messages.length - 1]
        if (tail && tail.role === 'user') {
          setEmptyResponseMsgId(tail.id)
          console.warn(
            `[EMPTY_RESPONSE] Creative: ${creativeId} (${creativeType}) — turn finished with no assistant message`
          )
          track(AnalyticsEvents.Composer.COMPOSER_EMPTY_RESPONSE, {
            creative_id: creativeId,
            creative_type: creativeType,
            total_message_count: messages.length,
            new_assistant_message_count: 0,
            last_message_part_types: 'none',
            all_new_part_types: 'none',
          })
        }
      }
    }
    prevLoadingRef.current = isLoading
  }, [
    isLoading,
    messages,
    creativeId,
    creativeType,
    track,
    initialMessageIds,
    totalCredits,
    setTyping,
  ])

  // Determine if the assistant has started responding
  // (last message is from assistant, or we're still waiting)
  const lastMsg = messages[messages.length - 1]
  const waitingForResponse =
    isLoading &&
    (lastMsg?.role === 'user' ||
      (lastMsg?.role === 'assistant' && (!lastMsg.parts || lastMsg.parts.length === 0)))

  // Show typing indicator when global store says agent is typing but
  // there's no active SSE stream (user returned mid-processing)
  const globalTyping = useChatStatusStore(s => s.typingCreatives.has(creativeId))
  const globalTools = useChatStatusStore(s => s.typingCreatives.get(creativeId))
  const showGlobalTyping = globalTyping && !isLoading

  // Derive tool-specific label/icon for the global typing indicator
  const globalToolName =
    showGlobalTyping && globalTools?.length ? globalTools[globalTools.length - 1] : null
  const globalToolMeta = globalToolName ? TOOL_META[globalToolName] : null

  // While SocialKit streams its live steps, the GenerationSteps ladder IS the
  // "working" indicator — suppress the generic thinking bubbles so they don't double up.
  const phaseActive = useGenerationPhaseStore(s => !!s.byCreative[creativeId]?.active)

  // First-creation narration: while a brand-new post is being generated (before any chat
  // history exists), the agent must NEVER sit silent on the idle "I can help with"
  // placeholder. We detect the in-progress / failed state across BOTH engines (SocialKit
  // beats, the generation-status store, and the suggestion status the topic/onboarding path
  // writes) so the panel always shows a working state, then the welcome (or a retry note).
  const suggestionStatus = useCreativeStore(
    s => s.creatives.find(c => c.id === creativeId)?.suggestionStatus
  )
  const genStatus = useGenerationStatusStore(s => s.statuses[creativeId]?.status)
  // A persisted score is proof the post generated successfully — it's the strongest "done"
  // signal and it's independent of the (sometimes-stale) status flags below.
  const hasScore = useCreativeProcessStore(s => !!s.byCreative[creativeId]?.snapshot)
  const noHistory = messages.length === 0
  // The authoritative result wins over stale flags. A success (genStatus 'ready' OR a real
  // score) means the post is done even if a stale building signal (a missed finalize beat left
  // phaseActive true) lingers — so the composer never hangs on "Creating your post…". And it
  // must NEVER show "I couldn't finish this post" for a post that actually succeeded: a stale
  // suggestionStatus='failed' (e.g. a sibling variant failed while THIS one scored 94) must not
  // override a real score/ready. But an EXPLICIT genStatus='failed' is authoritative — a stale
  // score snapshot from a prior attempt must not mask a real failure of THIS creative.
  const genSucceeded = genStatus !== 'failed' && (genStatus === 'ready' || hasScore)
  const genSettled = genSucceeded || genStatus === 'failed'
  const firstCreationActive =
    noHistory &&
    !genSettled &&
    (phaseActive || genStatus === 'generating' || suggestionStatus === 'generating')
  const firstCreationFailed =
    noHistory &&
    !firstCreationActive &&
    !genSucceeded &&
    (genStatus === 'failed' || suggestionStatus === 'failed')
  // Shape-aware noun so the working line reads naturally for every post type.
  const creatingNoun = /video|short|reel|tiktok|youtube/i.test(creativeType)
    ? 'reel'
    : /carousel/i.test(creativeType)
      ? 'carousel'
      : 'post'

  return (
    <div
      className="relative flex h-full flex-col bg-bg-2 text-paper"
      onDragEnter={e => {
        if (!e.dataTransfer?.types?.includes('Files')) return
        e.preventDefault()
        dragCounter.current += 1
        if (dragCounter.current === 1) setIsDragging(true)
      }}
      onDragOver={e => {
        if (!e.dataTransfer?.types?.includes('Files')) return
        e.preventDefault()
      }}
      onDragLeave={e => {
        if (!e.dataTransfer?.types?.includes('Files')) return
        dragCounter.current = Math.max(0, dragCounter.current - 1)
        if (dragCounter.current === 0) setIsDragging(false)
      }}
      onDrop={e => {
        if (!e.dataTransfer?.types?.includes('Files')) return
        e.preventDefault()
        dragCounter.current = 0
        setIsDragging(false)
        if (pending.length >= MAX_CHAT_ATTACHMENTS) return
        const files = Array.from(e.dataTransfer.files || []).filter(f =>
          f.type.startsWith('image/')
        )
        if (files.length === 0) return
        const room = MAX_CHAT_ATTACHMENTS - pending.length
        addFilesWithTracking(files.slice(0, room), 'drag')
      }}
    >
      {isDragging && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-acid bg-bg-2/90 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2 text-center text-paper">
            <Paperclip size={32} className="text-acid" />
            <p className="text-[14px] font-medium">
              {pending.length >= MAX_CHAT_ATTACHMENTS
                ? `Up to ${MAX_CHAT_ATTACHMENTS} images per message`
                : 'Drop image to attach'}
            </p>
          </div>
        </div>
      )}
      {showHeader && (
        <div className="flex-shrink-0 border-b border-line px-4 py-3">
          <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">Taka Agent</h2>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* The idle "I can help with" placeholder only belongs on a settled post with no
            chat yet. During a first creation (generating or just-failed) the agent narrates
            instead — see the working/failed blocks below — so it's never silent. */}
        {messages.length === 0 && !isLoading && !firstCreationActive && !firstCreationFailed && (
          <ChatEmptyState creativeId={creativeId} creativeType={creativeType} />
        )}
        {/* First creation in progress with no live SocialKit beats yet (pre-first-beat, or
            the in-house engine which streams none): keep the agent talking with a working
            line. Once beats arrive, GenerationSteps below takes over (phaseActive). */}
        {firstCreationActive && !phaseActive && (
          <ThinkingBubble label={`Creating your ${creatingNoun}…`} />
        )}
        {/* First creation failed before any welcome: say so and invite a retry via chat,
            rather than leaving a silent skeleton + empty panel. */}
        {firstCreationFailed && (
          <div className="mb-3 flex justify-start">
            <div className="alert max-w-[85%]" data-tone="warning" role="status">
              <div className="mb-1.5 flex items-center gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0" />
                <span className="font-medium">I couldn't finish this {creatingNoun}</span>
              </div>
              <p className="text-[12px] opacity-90">
                Tell me what you'd like and I'll create it, or tweak the brief and try again.
              </p>
            </div>
          </div>
        )}
        {messages.map((msg, msgIdx) => (
          <ChatMessageBubble
            // Approvals are answerable ONLY on the LAST message: the SDK's
            // addToolApprovalResponse mutates the last message and its
            // auto-resubmit checks it, so a superseded card's tap is a silent
            // no-op (renders expired instead). Restored-from-history is fine
            // WHEN last — the server only expires approvals on older messages,
            // so a reopened panel keeps a pending approval answerable.
            onApprovalResponse={
              msgIdx === messages.length - 1 ? addToolApprovalResponse : undefined
            }
            key={msg.id}
            message={msg as { id: string; role: string; parts: Record<string, unknown>[] }}
            onToolResult={initialMessageIds.has(msg.id) ? undefined : handleToolResult}
          />
        ))}
        {emptyResponseMsgId && !isLoading && (
          <div className="mb-3 flex justify-start">
            <div className="alert max-w-[85%]" data-tone="warning" role="status">
              <div className="mb-1.5 flex items-center gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0" />
                <span className="font-medium">Something went wrong</span>
              </div>
              <p className="mb-2 text-[12px] opacity-90">
                The AI didn't generate a response. This can happen occasionally.
              </p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 rounded-[6px] border border-line-bright px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-bg-3"
              >
                <RefreshCw size={12} />
                Try again
              </button>
            </div>
          </div>
        )}
        {/* A transport/server failure (e.g. a 5xx during a deploy window) rejects the chat
            POST. Without this the turn fails SILENTLY — the user's message just sits there
            with no reply, no error, no way to retry. Surface it with a one-tap retry that
            resends the last user message (handleRetry). */}
        {chatError && !isLoading && !emptyResponseMsgId && (
          <div className="mb-3 flex justify-start">
            <div className="alert max-w-[85%]" data-tone="error" role="alert">
              <div className="mb-1.5 flex items-center gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0" />
                <span className="font-medium">Taka couldn't respond</span>
              </div>
              <p className="mb-2 text-[12px] opacity-90">
                Something interrupted that request. Your message is safe — try sending it again.
              </p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 rounded-[6px] border border-line-bright px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-bg-3"
              >
                <RefreshCw size={12} />
                Try again
              </button>
            </div>
          </div>
        )}
        {waitingForResponse && !phaseActive && <ThinkingBubble />}
        {showGlobalTyping && !phaseActive && (
          <ThinkingBubble icon={globalToolMeta?.icon} label={globalToolMeta?.activeLabel} />
        )}
        {creativeId && <GenerationSteps creativeId={creativeId} />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3">
        {queuedSends.length > 0 && (
          <div className="mb-2 rounded-[10px] border border-line bg-bg-2 px-2.5 py-2">
            <div className="mono mb-1 text-[10px] tracking-[0.08em] text-paper-faint uppercase">
              Sends when Taka finishes
            </div>
            {queuedSends.map(q => (
              <div key={q.id} className="flex items-center gap-2 py-1 text-[12.5px] text-paper-dim">
                <span className="min-w-0 flex-1 truncate">{q.text}</span>
                <button
                  type="button"
                  onClick={() => setQueuedSends(qs => qs.filter(x => x.id !== q.id))}
                  aria-label="Cancel queued message"
                  className="rounded-[6px] p-1 text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        {outOfCredits ? (
          <ComposerCreditBlock />
        ) : (
          <>
            {/* Hidden file input — accepts multi-select */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => {
                const files = Array.from(e.target.files || [])
                if (files.length > 0) addFilesWithTracking(files, 'button')
                e.target.value = ''
              }}
            />

            <ComposerRow
              onSubmit={handleSubmit}
              leading={
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={readOnly || isLoading || pending.length >= MAX_CHAT_ATTACHMENTS}
                  className="my-1 inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper disabled:text-paper-faint"
                  title={
                    pending.length >= MAX_CHAT_ATTACHMENTS
                      ? `Up to ${MAX_CHAT_ATTACHMENTS} images per message`
                      : 'Attach image'
                  }
                >
                  <Paperclip size={16} />
                </button>
              }
              trailing={
                <ComposerSendButton
                  loading={isLoading}
                  disabled={
                    readOnly || (!input.trim() && ready.length === 0) || isLoading || hasUploading
                  }
                />
              }
              above={
                sectionContext || pending.length > 0 ? (
                  /* Inline attachments (inside the input bar). The section
                 context chip uses AnimatePresence + a layoutId tied to
                 the creativeId + sectionType so future preview-side
                 wrappers with the same layoutId can fly INTO the chip
                 (Framer interpolates the source's bounding box into the
                 chip's). For now the entry just slides in from below
                 with a soft acid arrival. */
                  <div className="space-y-1.5">
                    <AnimatePresence>
                      {sectionContext && (
                        <motion.div
                          key={`section-ctx-${sectionContext.sectionId || sectionContext.sectionType}`}
                          layoutId={`section-handoff-${creativeId}-${sectionContext.sectionType}`}
                          initial={{ opacity: 0, y: 8, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 4, scale: 0.96 }}
                          transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                        >
                          <SectionContextChip
                            context={sectionContext}
                            onDismiss={() => clearSectionContext(creativeId)}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {pending.length > 0 && (
                      <div className="flex flex-wrap items-start gap-2">
                        {pending.map(p => (
                          <div key={p.localId} className="relative">
                            <img
                              src={p.previewUrl}
                              alt="Upload preview"
                              className={`h-14 w-14 rounded-lg object-cover ${
                                p.status === 'failed' ? 'opacity-50' : ''
                              }`}
                            />
                            {p.status === 'uploading' && (
                              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30">
                                <Loader2 size={16} className="animate-spin text-white" />
                              </div>
                            )}
                            {p.status === 'failed' && (
                              <div
                                className="absolute inset-0 flex items-center justify-center rounded-lg bg-[color:var(--alert-error-bg)]/60"
                                title={p.error || 'Upload failed'}
                              >
                                <AlertTriangle
                                  size={14}
                                  className="text-[color:var(--alert-error-text)]"
                                />
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => removeAt(p.localId)}
                              className="absolute -top-1.5 -right-1.5 rounded-full border border-line-bright bg-bg-4 p-0.5 text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : undefined
              }
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => {
                  setInput(e.target.value)
                  const el = e.target
                  el.style.height = 'auto'
                  const lineHeight = 20
                  el.style.height = `${Math.min(el.scrollHeight, lineHeight * 6)}px`
                }}
                onKeyDown={handleKeyDown}
                disabled={readOnly}
                placeholder={
                  readOnly
                    ? 'This post is live and can’t be edited.'
                    : CHAT_PLACEHOLDERS[creativeType]
                }
                rows={1}
                className="w-full resize-none bg-transparent py-2.5 text-[13.5px] leading-5 text-paper outline-none placeholder:text-paper-faint"
              />
            </ComposerRow>
          </>
        )}
      </div>
    </div>
  )
}
